import { z } from 'zod';
import { apiFetch, ApiError } from '@/lib/api';

/**
 * Hardware-replacement approval queue — the client mirror of the
 * `/api/fingerprint-requests*` admin handlers
 * (`rust/server/src/routes/fingerprint_requests.rs`, #177).
 *
 * A pending request is the footprint of a refused machine verify whose presented
 * hardware composition matched no live seat. An admin either approves it (the licence
 * is re-pinned onto the new composition) or dismisses it (nothing changes).
 */
export const fingerprintComponentSchema = z.object({
  componentType: z.string(),
  componentValue: z.string(),
  matched: z.boolean(),
});

export const fingerprintChangeRequestSchema = z.object({
  id: z.string(),
  licenseId: z.string(),
  candidateActivationId: z.string().nullable(),
  installationId: z.string().nullable(),
  reportedSerial: z.string().nullable(),
  presentedHash: z.string(),
  components: z.array(fingerprintComponentSchema),
  matchedCount: z.number(),
  status: z.enum(['pending', 'approved', 'dismissed']),
  requestCount: z.number(),
  createdAt: z.string(),
  lastSeenAt: z.string(),
  resolvedAt: z.string().nullable(),
  resolvedBy: z.string().nullable(),
});

export const fingerprintChangeRequestListSchema = z.object({
  data: z.array(fingerprintChangeRequestSchema),
});

export type FingerprintComponent = z.infer<typeof fingerprintComponentSchema>;
export type FingerprintChangeRequest = z.infer<typeof fingerprintChangeRequestSchema>;

/** `GET /api/fingerprint-requests` — the pending queue (optionally one licence). */
export async function fetchFingerprintChangeRequests(
  licenseId?: string
): Promise<FingerprintChangeRequest[]> {
  const query = licenseId ? `?license_id=${encodeURIComponent(licenseId)}` : '';
  const response = await apiFetch(`/fingerprint-requests${query}`);
  if (!response.ok) {
    throw new ApiError('Failed to load fingerprint change requests', response.status);
  }
  const body = await response.json();
  return fingerprintChangeRequestListSchema.parse(body).data;
}

/** `POST /api/fingerprint-requests/:id/approve` — re-pin the licence. */
export async function approveFingerprintChangeRequest(
  id: string
): Promise<FingerprintChangeRequest> {
  const response = await apiFetch(`/fingerprint-requests/${id}/approve`, { method: 'POST' });
  if (!response.ok) {
    throw new ApiError('Failed to approve fingerprint change request', response.status);
  }
  return fingerprintChangeRequestSchema.parse(await response.json());
}

/** `POST /api/fingerprint-requests/:id/dismiss` — close without touching a fingerprint. */
export async function dismissFingerprintChangeRequest(
  id: string
): Promise<FingerprintChangeRequest> {
  const response = await apiFetch(`/fingerprint-requests/${id}/dismiss`, { method: 'POST' });
  if (!response.ok) {
    throw new ApiError('Failed to dismiss fingerprint change request', response.status);
  }
  return fingerprintChangeRequestSchema.parse(await response.json());
}
